/**
 * Reine Auswahlrechnung — ohne React, damit Zeitraumlogik ohne Rendern pruefbar bleibt.
 */
import { addIsoDays, diffIsoDays } from './calendarDates';

/** Serverseitige Obergrenze eines Batch-Pushs (`MaxRangesPerBatch`). */
export const MAX_RANGES = 50;

export interface SelectedRange {
  id: string;
  from: string;
  to: string;
}

export type SelectionMode = 'replace' | 'append' | 'extend';

export function normalizeRange(anchor: string, cursor: string): { from: string; to: string } {
  return anchor <= cursor ? { from: anchor, to: cursor } : { from: cursor, to: anchor };
}

export function applyDraft(
  existing: readonly SelectedRange[],
  draft: { from: string; to: string },
  mode: SelectionMode,
  id: string,
): SelectedRange[] {
  const entry = { id, ...draft };
  if (mode === 'replace') return [entry];
  if (mode === 'extend') return [...existing.slice(0, -1), entry];
  // Ein weiterer Zeitraum, der einen vorhandenen überlappt oder berührt, verschmilzt mit ihm.
  // Sonst zählten Nächte doppelt, und dieselben Tage gingen zweimal an den Server.
  const touching = existing.filter(
    (range) => range.from <= addIsoDays(draft.to, 1) && range.to >= addIsoDays(draft.from, -1),
  );
  if (touching.length === 0) {
    return existing.length >= MAX_RANGES ? [...existing] : [...existing, entry];
  }
  const merged = {
    id: touching[0]!.id,
    from: [draft.from, ...touching.map((range) => range.from)].sort()[0]!,
    to: [draft.to, ...touching.map((range) => range.to)].sort().at(-1)!,
  };
  return [...existing.filter((range) => !touching.includes(range)), merged];
}

export function rangeIndexAt(ranges: readonly SelectedRange[], date: string): number {
  return ranges.findIndex((range) => range.from <= date && range.to >= date);
}

export function totalNights(ranges: readonly SelectedRange[]): number {
  return ranges.reduce((sum, range) => sum + diffIsoDays(range.from, range.to) + 1, 0);
}
